import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useHotel } from "../contexts/HotelContext";
import { useToast } from "../contexts/ToastContext";
import {
  impersonateUser,
  stopImpersonation,
  ImpersonationResponse,
} from "../services/tenantApi";

/**
 * Hook for managing user impersonation functionality.
 * Only available to global admin users.
 */
export const useImpersonation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, setToken, refreshUser, isImpersonating } = useHotel();
  const { addToast } = useToast();

  /**
   * Start impersonating a user.
   * Replaces current session with impersonated user's session.
   */
  const startImpersonationMutation = useMutation({
    mutationFn: (userId: string) => impersonateUser(userId),
    onSuccess: (response: ImpersonationResponse) => {
      // Store the new token
      setToken(response.access_token);

      // Clear all cached queries to get fresh data for impersonated user
      queryClient.clear();

      // Refresh user context with new user data
      refreshUser();

      addToast({
        message: `Zalogowano jako ${response.user.email}`,
        type: "success",
      });

      // Navigate to dashboard
      navigate("/");
    },
    onError: (error: any) => {
      console.error("Impersonation failed:", error);
      const message =
        error.response?.data?.message || "Nie udało się zalogować jako użytkownik";
      addToast({
        message,
        type: "error",
      });
    },
  });

  /**
   * Stop impersonating and return to admin account.
   */
  const stopImpersonationMutation = useMutation({
    mutationFn: () => stopImpersonation(),
    onSuccess: (response: ImpersonationResponse) => {
      // Store the admin token
      setToken(response.access_token);

      // Clear all cached queries
      queryClient.clear();

      // Refresh user context
      refreshUser();

      addToast({
        message: "Powrócono do konta administratora",
        type: "success",
      });

      // Navigate to admin tenants page
      navigate("/admin/tenants");
    },
    onError: (error: any) => {
      console.error("Stop impersonation failed:", error);
      const message =
        error.response?.data?.message || "Nie udało się zakończyć impersonacji";
      addToast({
        message,
        type: "error",
      });
    },
  });

  return {
    /**
     * Start impersonating a user by their ID
     */
    startImpersonation: (userId: string) =>
      startImpersonationMutation.mutate(userId),

    /**
     * Stop impersonating and return to admin
     */
    stopImpersonation: () => stopImpersonationMutation.mutate(),

    /**
     * Whether a start impersonation request is in progress
     */
    isStarting: startImpersonationMutation.isPending,

    /**
     * Whether a stop impersonation request is in progress
     */
    isStopping: stopImpersonationMutation.isPending,

    /**
     * Whether the current user is impersonating someone
     */
    isImpersonating,

    /**
     * Whether current user can impersonate others (is admin and not already impersonating)
     */
    canImpersonate: user?.role === "admin" && !isImpersonating,
  };
};
